/**
 * Manufacturing → CM PO
 *
 * Lists contract manufacturing purchase orders. Creation supports multiple work orders per
 * purchase order line; work order SKU can differ from the PO line SKU. Each work order uses the same
 * quantity as that PO line. External labour across work orders follows the labour budget unless the supplier
 * does not migrate labour (Suntronic). A “Create CM PO”
 * button opens a creation modal; clicking a row opens a detail modal. The create modal can save a
 * draft in the browser until submit—no purchase order or work order is created until you submit.
 */

import { createPortal } from "react-dom";
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, ExternalLink, FileDown, HelpCircle, Plus, Trash2, X } from "lucide-react";

import { Badge, type BadgeTone } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { HubSaveButton } from "@/components/ui/HubSaveButton";
import { Input, Label, TextArea } from "@/components/ui/Input";
import { AssemblySkuCombobox, mrpSkuMasterRowDescriptionLine } from "@/components/mrp/AssemblySkuCombobox";
import { PurchaseOrderServiceSkuWhen } from "@/components/mrp/PurchaseOrderServiceSkuWhen";
import { SupplierSearchableSelect } from "@/components/mrp/SupplierSearchableSelect";
import { ApiError, apiFetch } from "@/lib/api";
import { downloadPurchaseOrderPdf } from "@/lib/purchaseOrderPdfApi";
import { OperationalLocationNativeSelectOptions } from "@/components/mrp/TransferEndpointLocationSelect";
import type { MrpSkuDetail, MrpSkuMasterRow } from "@/lib/mrp";
import { formatServiceSkuUnitPriceAutofill } from "@integrator-src/mrp/purchase-order-service-sku-price";
import { useManufacturingScopeHubWrite } from "@/lib/useHubModuleWrite";
import { useOperationalLocations } from "@/lib/transferEndpointLocations";
import { HoverHintContent, ReadOnlyHoverLayer } from "@/components/ui/title-help";

import type {
  CmPoLineDraft,
  CmPoItemRecord,
  CmPoRecord,
  CmPoWorkOrderDraft,
  CmPurchaseOrderCancelEligibility,
  CmWorkOrderRecord,
} from "./cmPurchaseOrderTypes";
import { formatCmPoLocationSubtitle, uniqueWorkOrderLocationNames } from "./cmPurchaseOrderLocations";

// ─── Constants ────────────────────────────────────────────────────────────────

/** Matches `supplier.data.id` Suntronic entry used in CM backfill tooling (with name heuristic). */
const SUNTRONIC_SUPPLIER_DATA_IDS = new Set<number>([1734]);

/** Default ship-to when no draft is stored (Smart Grid Solutions). */
const DEFAULT_CM_PO_SHIP_TO_LOCATION_ID = 1;

const CURRENCY_OPTIONS = [
  { value: "USD", label: "USD" },
  { value: "CNY", label: "CNY" },
] as const;

type Currency = (typeof CURRENCY_OPTIONS)[number]["value"];

export function cmPurchaseOrderIdFromSearchParams(searchParams: URLSearchParams): number | null {
  const raw = searchParams.get("cm_po_id")?.trim() ?? "";
  if (!/^\d+$/.test(raw)) return null;
  const id = Number.parseInt(raw, 10);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
}

function emptyWo(): CmPoWorkOrderDraft {
  return { sku: "", labour: "", finished_goods_disposition: "transfer" };
}

function emptyLine(): CmPoLineDraft {
  return {
    po_sku: "",
    quantity: "",
    unit_price: "",
    line_description: "",
    work_orders: [emptyWo()],
  };
}

/** When every finished-good row is blank, saved CM PO SKU presets may populate work-order rows. */
function woDraftFinishedGoodSkusAllBlank(workOrders: CmPoWorkOrderDraft[]): boolean {
  return workOrders.every((draft) => draft.sku.trim() === "");
}

function composeWorkOrdersFromPresetSkus(workOrderSkus: string[]): CmPoWorkOrderDraft[] {
  return workOrderSkus.map((sku) => ({ sku, labour: "", finished_goods_disposition: "transfer" as const }));
}

function presetWorkOrdersForComposeLine(
  savedRows:
    | { purchase_order_sku: string; work_order_skus: string[] }[]
    | undefined,
  purchaseOrderSkuTrimmed: string,
): CmPoWorkOrderDraft[] | null {
  if (!savedRows?.length || purchaseOrderSkuTrimmed.trim().length === 0) {
    return null;
  }
  const needle = purchaseOrderSkuTrimmed.trim().toLowerCase();
  const hit = savedRows.find((row) => row.purchase_order_sku.trim().toLowerCase() === needle);
  if (!hit || hit.work_order_skus.length === 0) {
    return null;
  }
  return composeWorkOrdersFromPresetSkus(hit.work_order_skus);
}

/** Browser-only CM PO compose form draft (Create modal). Purchase orders and work orders are not created until submit. */
const CM_PO_CREATE_DRAFT_STORAGE_KEY = "hub.manufacturing.cmPoCreateDraft.v1";

interface CmPoCreateDraftStoredV1 {
  v: 1;
  savedAtIso: string;
  supplierSearch: string;
  supplierId: string;
  supplierLabel: string | null;
  locationId: number;
  currency: Currency;
  comments: string;
  lines: CmPoLineDraft[];
}

interface CmPoCreateModalBootstrap {
  supplierSearch: string;
  supplierId: string;
  supplierLabel: string | null;
  locationId: number;
  currency: Currency;
  comments: string;
  lines: CmPoLineDraft[];
  restoredMeaningfulDraft: boolean;
}

function coerceLocationDraftId(raw: unknown): number {
  const n = typeof raw === "number" ? raw : Number(raw);
  if (Number.isFinite(n) && n > 0) return Math.trunc(n);
  return DEFAULT_CM_PO_SHIP_TO_LOCATION_ID;
}

function coerceCurrencyDraft(raw: unknown): Currency {
  const s = typeof raw === "string" ? raw : "";
  return CURRENCY_OPTIONS.some((option) => option.value === s) ? (s as Currency) : "USD";
}

function coerceCmPoWorkOrderDraft(row: unknown): CmPoWorkOrderDraft | null {
  if (!row || typeof row !== "object") return null;
  const wo = row as Record<string, unknown>;
  return {
    sku: typeof wo.sku === "string" ? wo.sku : "",
    labour: typeof wo.labour === "string" ? wo.labour : "",
    finished_goods_disposition: wo.finished_goods_disposition === "stay" ? "stay" : "transfer",
  };
}

function coerceCmPoLineDraft(row: unknown): CmPoLineDraft | null {
  if (!row || typeof row !== "object") return null;
  const line = row as Record<string, unknown>;
  const wosRaw = Array.isArray(line.work_orders) ? line.work_orders : [];
  const work_orders = wosRaw
    .map((element) => coerceCmPoWorkOrderDraft(element))
    .filter((w): w is CmPoWorkOrderDraft => w != null);
  return {
    po_sku: typeof line.po_sku === "string" ? line.po_sku : "",
    quantity: typeof line.quantity === "string" ? line.quantity : "",
    unit_price: typeof line.unit_price === "string" ? line.unit_price : "",
    line_description:
      typeof line.line_description === "string" ? line.line_description : "",
    work_orders: work_orders.length > 0 ? work_orders : [emptyWo()],
  };
}

function coerceCmPoLines(raw: unknown): CmPoLineDraft[] | null {
  if (!Array.isArray(raw)) return null;
  const lines = raw.map((row) => coerceCmPoLineDraft(row)).filter((l): l is CmPoLineDraft => l != null);
  return lines.length > 0 ? lines : null;
}

function snapshotIsEmptyForDraftAutosave(
  snapshot: Pick<
    CmPoCreateDraftStoredV1,
    "supplierSearch" | "supplierId" | "locationId" | "currency" | "comments" | "lines"
  >,
): boolean {
  if (snapshot.supplierId.trim() !== "") return false;
  if (snapshot.comments.trim() !== "") return false;
  if (snapshot.supplierSearch.trim() !== "") return false;
  if (snapshot.currency !== "USD") return false;
  if (snapshot.locationId !== DEFAULT_CM_PO_SHIP_TO_LOCATION_ID) return false;
  if (snapshot.lines.length !== 1) return false;
  const lone = snapshot.lines[0];
  if (!lone) return true;
  if (lone.po_sku.trim() !== "") return false;
  if (lone.quantity.trim() !== "") return false;
  if (lone.unit_price.trim() !== "") return false;
  if (lone.line_description.trim() !== "") return false;
  if (lone.work_orders.length !== 1) return false;
  const loneWo = lone.work_orders[0];
  if (!loneWo || loneWo.sku.trim() !== "" || loneWo.labour.trim() !== "") return false;
  return true;
}

function readCmPoCreateDraft(): CmPoCreateDraftStoredV1 | null {
  try {
    const rawJson = window.localStorage.getItem(CM_PO_CREATE_DRAFT_STORAGE_KEY);
    if (rawJson == null || rawJson.length === 0) return null;
    const parsedUnknown: unknown = JSON.parse(rawJson);
    if (!parsedUnknown || typeof parsedUnknown !== "object") return null;
    const parsed = parsedUnknown as Record<string, unknown>;
    if (parsed.v !== 1) return null;
    const lines = coerceCmPoLines(parsed.lines);
    if (!lines) return null;
    return {
      v: 1,
      savedAtIso: typeof parsed.savedAtIso === "string" ? parsed.savedAtIso : new Date().toISOString(),
      supplierSearch: typeof parsed.supplierSearch === "string" ? parsed.supplierSearch : "",
      supplierId: typeof parsed.supplierId === "string" ? parsed.supplierId : "",
      supplierLabel: typeof parsed.supplierLabel === "string" ? parsed.supplierLabel : null,
      locationId: coerceLocationDraftId(parsed.locationId),
      currency: coerceCurrencyDraft(parsed.currency),
      comments: typeof parsed.comments === "string" ? parsed.comments : "",
      lines,
    };
  } catch {
    return null;
  }
}

function persistCmPoCreateDraft(snapshot: CmPoCreateDraftStoredV1): void {
  try {
    if (snapshotIsEmptyForDraftAutosave(snapshot)) {
      window.localStorage.removeItem(CM_PO_CREATE_DRAFT_STORAGE_KEY);
      return;
    }
    window.localStorage.setItem(CM_PO_CREATE_DRAFT_STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    /* private mode / quota */
  }
}

function clearCmPoCreateDraftStorage(): void {
  try {
    window.localStorage.removeItem(CM_PO_CREATE_DRAFT_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

function loadCmPoCreateModalBootstrap(): CmPoCreateModalBootstrap {
  const stored = readCmPoCreateDraft();
  if (!stored) {
    return {
      supplierSearch: "",
      supplierId: "",
      supplierLabel: null,
      locationId: DEFAULT_CM_PO_SHIP_TO_LOCATION_ID,
      currency: "USD",
      comments: "",
      lines: [emptyLine()],
      restoredMeaningfulDraft: false,
    };
  }
  const meaningfulDraft = !snapshotIsEmptyForDraftAutosave(stored);
  return {
    supplierSearch: stored.supplierSearch,
    supplierId: stored.supplierId,
    supplierLabel: stored.supplierLabel,
    locationId: stored.locationId,
    currency: stored.currency,
    comments: stored.comments,
    lines: stored.lines.map((line) => ({
      ...line,
      work_orders: line.work_orders.map((w) => ({ ...w })),
    })),
    restoredMeaningfulDraft: meaningfulDraft,
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function lineTotal(line: CmPoLineDraft): number | null {
  const qty = Number.parseFloat(line.quantity);
  const price = Number.parseFloat(line.unit_price);
  if (!Number.isFinite(qty) || qty <= 0) return null;
  if (!Number.isFinite(price) || price < 0) return null;
  return Math.round(qty * price * 100) / 100;
}

function nonemptyWorkOrders(drafts: CmPoWorkOrderDraft[]): CmPoWorkOrderDraft[] {
  return drafts.filter((row) => row.sku.trim() !== "" || row.labour.trim() !== "");
}

/** Builds the Megaventory purchase order row description for the Purchase order SKU HoverHintContent and POST body. */
function previewMegaventoryRowDescription(line: CmPoLineDraft): string {
  const woSkus = nonemptyWorkOrders(line.work_orders)
    .map((w) => w.sku.trim())
    .filter((s) => s.length > 0);
  const tail = line.line_description.trim();
  const segments = [...woSkus];
  if (tail.length > 0) {
    segments.push(tail);
  }
  return segments.join(" | ");
}

/** Suntronic: BoM labour SKU carries cost — do not push PO line totals onto work orders. */
function supplierUsesBomLabourNotPoMigrate(supplierDataId: number, mvSupplierName: string | null): boolean {
  if (SUNTRONIC_SUPPLIER_DATA_IDS.has(supplierDataId)) return true;
  const trimmed = (mvSupplierName ?? "").trim();
  return trimmed.length > 0 && /\bsuntronic\b/i.test(trimmed);
}

function lineQuantityForInstructions(line: CmPoLineDraft): number {
  const q = Number.parseFloat(String(line.quantity).trim());
  return Number.isFinite(q) && q > 0 ? q : 0;
}

function sumPoQtyForLines(
  lines: CmPoLineDraft[],
  matches: (poSku: string) => boolean,
): number {
  return lines.reduce((sum, line) => {
    const sku = line.po_sku.trim();
    if (!sku) return sum;
    return matches(sku) ? sum + lineQuantityForInstructions(line) : sum;
  }, 0);
}

/** `FI-5A B4` or `FI-5A-B4` (finished-good label, not the FI-5A-*-TLA-* family). */
function isFi5aB4Sku(skuRaw: string): boolean {
  const compact = skuRaw.trim().replace(/\s+/g, " ");
  return /^FI-5A\s*B4$/i.test(compact) || /^FI-5A-B4$/i.test(compact.replace(/\s/g, ""));
}

/** `FI-5A-<segment>-TLA` with optional trailing segments (for example `FI-5A-W-TLA` or `FI-5A-x-TLA-y`). */
function isFi5aAnyTlaSku(skuRaw: string): boolean {
  const sku = skuRaw.trim();
  return /^FI-5A-[^-]+-TLA(?:-.+|)$/i.test(sku);
}

/** `FI-3C-<segment>-TLA` with optional trailing segments (matches BorQ and Q families too). */
function isFi3cAnyTlaSku(skuRaw: string): boolean {
  const sku = skuRaw.trim();
  return /^FI-3C-[^-]+-TLA(?:-.+|)$/i.test(sku);
}

function isFi3cBorqTlaSku(skuRaw: string): boolean {
  const sku = skuRaw.trim();
  return /^FI-3C-borq-tla(?:-.+|)$/i.test(sku);
}

function isFi3cQTlaSku(skuRaw: string): boolean {
  const sku = skuRaw.trim();
  return /^FI-3C-Q-TLA(?:-.+|)$/i.test(sku);
}

function isFi5aWTlaSku(skuRaw: string): boolean {
  const sku = skuRaw.trim();
  return /^FI-5A-W-TLA(?:-.+|)$/i.test(sku);
}

function formatInstructionQty(n: number): string {
  if (!Number.isFinite(n)) return "0";
  const rounded = Math.round(n * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : String(rounded);
}

/**
 * Megaventory purchase order comment block for Suntronic: quantity rollups by PO line SKU family.
 * Omits any code line whose computed total is zero.
 */
function buildSuntronicSpecialInstructions(lines: CmPoLineDraft[]): string {
  const q3cTla = sumPoQtyForLines(lines, isFi3cAnyTlaSku);
  const q5aTla = sumPoQtyForLines(lines, isFi5aAnyTlaSku);
  const q5aB4 = sumPoQtyForLines(lines, isFi5aB4Sku);
  const qBorq = sumPoQtyForLines(lines, isFi3cBorqTlaSku);
  const q5aW = sumPoQtyForLines(lines, isFi5aWTlaSku);
  const q3cQ = sumPoQtyForLines(lines, isFi3cQTlaSku);

  const tl5903 = q3cTla + q5aTla + q5aB4;
  const tl5920 = q5aTla + q5aB4;

  const parts: string[] = [];
  const push = (label: string, n: number) => {
    if (n > 0) {
      parts.push(`${label}: ${formatInstructionQty(n)}`);
    }
  };

  push("TL-5903/T", tl5903);
  push("TL-5920/T", tl5920);
  push("BAT-2025", qBorq);
  push("CT-2123", q5aW);
  push("CT-2124", q3cQ);

  return parts.join("\n");
}

function formatCurrency(value: number): string {
  return value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function statusTone(status: string | null): BadgeTone {
  const s = (status ?? "").toLowerCase();
  if (s === "pending" || s === "open") return "neutral";
  if (s === "verified" || s === "approved") return "info";
  if (s.includes("partial")) return "warning";
  if (s === "closed" || s === "fullyinvoiced" || s === "received") return "success";
  if (s.includes("cancel")) return "danger";
  return "neutral";
}

function woStatusTone(status: string | null): BadgeTone {
  const s = (status ?? "").toLowerCase();
  if (s === "open" || s === "pending") return "neutral";
  if (s === "closed" || s === "completed") return "success";
  if (s.includes("cancel")) return "danger";
  return "neutral";
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function mvPurchaseOrderUrl(mvId: number | null, mvNumber: string | null): string | null {
  if (mvId == null) return null;
  const name = encodeURIComponent(mvNumber ?? String(mvId));
  return `https://smartgridsolutions.megaventory.com/inventory/default.aspx?document=PurchaseOrder&id=${mvId}&name=${name}`;
}

function mvWorkOrderUrl(mvWorkOrderId: number, mvWorkOrderNumber: string | null): string {
  const name = encodeURIComponent(`Build Order ${mvWorkOrderNumber ?? ""}`);
  return `https://smartgridsolutions.megaventory.com/inventory/default.aspx?document=WorkOrder&id=${mvWorkOrderId}&name=${name}`;
}

function CmPoPdfLink({
  cmPoId,
  publicPurchaseOrderId,
  purchaseOrderNumber,
  iconClassName,
  stopRowClick = false,
}: {
  cmPoId: number;
  publicPurchaseOrderId: number;
  purchaseOrderNumber: string | null;
  iconClassName: string;
  stopRowClick?: boolean;
}) {
  const pdfMutation = useMutation({
    mutationFn: () =>
      apiFetch<{ pdfBase64: string }>(`/api/manufacturing/cm-purchase-orders/${String(cmPoId)}/pdf`, {
        method: "POST",
        body: {},
      }).then((response) => response.pdfBase64),
    onSuccess: (pdfBase64) => {
      downloadPurchaseOrderPdf(pdfBase64, purchaseOrderNumber, publicPurchaseOrderId);
    },
  });

  if (!Number.isFinite(publicPurchaseOrderId) || publicPurchaseOrderId <= 0) {
    return null;
  }

  const errorMessage =
    pdfMutation.error instanceof Error ? pdfMutation.error.message : "Could not download PDF.";

  return (
    <button
      type="button"
      title={pdfMutation.isError ? errorMessage : "Download PDF"}
      aria-label="Download purchase order PDF"
      disabled={pdfMutation.isPending}
      onClick={(event) => {
        if (stopRowClick) {
          event.stopPropagation();
        }
        pdfMutation.mutate();
      }}
      className="text-slate-400 hover:text-brand-600 disabled:opacity-50"
    >
      <FileDown className={iconClassName} />
    </button>
  );
}

function formatStatus(status: string): string {
  const map: Record<string, string> = {
    FullyInvoiced: "Fully Invoiced",
    fullyinvoiced: "Fully Invoiced",
    PartiallyReceivedAndPartiallyInvoiced: "Partial",
    Cancelled: "Cancelled",
    cancelled: "Cancelled",
    canceled: "Cancelled",
    Closed: "Closed",
    closed: "Closed",
    Pending: "Pending",
    pending: "Pending",
    Verified: "Approved",
    verified: "Approved",
    Approved: "Approved",
    approved: "Approved",
    Open: "Open",
    draft: "Draft",
    Draft: "Draft",
  };
  return map[status] ?? status;
}

function isInactivePurchaseOrderStatus(status: string | null | undefined): boolean {
  const normalized = (status ?? "").trim().toLowerCase();
  return (
    normalized === "closed" ||
    normalized === "cancelled" ||
    normalized === "canceled" ||
    normalized === "fullyinvoiced"
  );
}

function cmPoItemReceivedQuantity(
  item: CmPoItemRecord,
  systemOfRecord: CmPoRecord["system_of_record"],
): number {
  if (systemOfRecord === "hub" && item.hub_quantity_received != null) {
    return item.hub_quantity_received;
  }
  return item.mv_quantity_received;
}

type CmPoStatusFilter = "active" | "all" | string;

// ─── Detail Modal ─────────────────────────────────────────────────────────────

const WO_PILL_TONE_CLASSES: Record<BadgeTone, string> = {
  neutral: "bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-200",
  muted: "bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100",
  success: "bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-200",
  danger:  "bg-red-100 text-red-800 border-red-200 hover:bg-red-200",
  warning: "bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-200",
  info:    "bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-200",
};

function WoPill({
  wo,
  canDrag,
  isDragging,
  onDragStart,
  onDragEnd,
}: {
  wo: CmWorkOrderRecord;
  canDrag: boolean;
  isDragging: boolean;
  onDragStart: (buildOrderId: number) => void;
  onDragEnd: () => void;
}) {
  const tone = woStatusTone(wo.mv_status);

  const received = wo.mv_received_quantity ?? 0;
  const remaining = Math.max(0, wo.quantity - received);

  const roleLabel = wo.link_role === "additional" ? "Additional labour charge" : "Primary CM PO";
  const hintLines = [
    roleLabel,
    wo.mv_status ? `Status: ${wo.mv_status}` : null,
    `SKU: ${wo.sku}`,
    `Qty: ${Number(wo.quantity).toLocaleString()}`,
    `Remaining: ${remaining.toLocaleString()}`,
    wo.link_role === "primary"
      ? `External labour: $${formatCurrency(wo.labour_cost)} (auto-syncs from PO line until receive)`
      : `External labour charge: $${formatCurrency(wo.labour_cost)}`,
  ]
    .filter(Boolean)
    .join("  ·  ");

  const pill = (
    <div
      draggable={canDrag}
      onDragStart={(e) => {
        e.dataTransfer.setData("text/plain", String(wo.public_build_order_id));
        e.dataTransfer.effectAllowed = "move";
        onDragStart(wo.public_build_order_id);
      }}
      onDragEnd={onDragEnd}
      className={[
        "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-mono font-medium select-none transition-all",
        WO_PILL_TONE_CLASSES[tone],
        canDrag ? "cursor-grab active:cursor-grabbing shadow-sm" : "",
        isDragging ? "opacity-40" : "",
      ].join(" ")}
    >
      {wo.mv_work_order_number ?? `WO #${wo.mv_work_order_id}`}
      {wo.link_role === "additional" ? (
        <span className="ml-1 rounded bg-amber-100 px-1 py-0 text-[10px] font-sans font-semibold uppercase text-amber-900">
          addl
        </span>
      ) : null}
    </div>
  );

  return (
    <HoverHintContent hoverHint={hintLines} cursor="default">
      <Link
        to={`/manufacturing-management/work-orders/${wo.public_build_order_id}`}
        className="inline-flex"
        onClick={(event) => event.stopPropagation()}
      >
        {pill}
      </Link>
    </HoverHintContent>
  );
}

function CmPoExternalLabourLinksPanel({
  workOrders,
  canWrite,
  onPatchLabour,
  patchPending,
  patchError,
}: {
  workOrders: CmWorkOrderRecord[];
  canWrite: boolean;
  onPatchLabour: (payload: { buildOrderId: number; chargeId: number; labourCost: number }) => void;
  patchPending: boolean;
  patchError: ApiError | null;
}) {
  const [drafts, setDrafts] = useState<Record<number, string>>({});

  useEffect(() => {
    const next: Record<number, string> = {};
    for (const workOrder of workOrders) {
      if (workOrder.link_role === "additional") {
        next[workOrder.id] = String(workOrder.labour_cost);
      }
    }
    setDrafts(next);
  }, [workOrders]);

  if (workOrders.length === 0) {
    return null;
  }

  return (
    <div className="overflow-hidden rounded-md border border-slate-200">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-3 py-2 text-left">Work order</th>
            <th className="px-3 py-2 text-left">Role</th>
            <th className="px-3 py-2 text-right">External labour</th>
            <th className="px-3 py-2 text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {workOrders.map((workOrder) => {
            const draft = Number(drafts[workOrder.id]);
            const dirty =
              workOrder.link_role === "additional" &&
              Number.isFinite(draft) &&
              Math.abs(draft - workOrder.labour_cost) >= 0.005;
            return (
              <tr key={workOrder.id}>
                <td className="px-3 py-2">
                  <Link
                    to={`/manufacturing-management/work-orders/${workOrder.public_build_order_id}`}
                    className="font-mono text-xs font-medium text-brand-700 hover:text-brand-800"
                  >
                    {workOrder.mv_work_order_number ?? `WO #${workOrder.public_build_order_id}`}
                  </Link>
                  <div className="text-xs text-slate-500">{workOrder.sku}</div>
                </td>
                <td className="px-3 py-2 capitalize text-slate-700">{workOrder.link_role}</td>
                <td className="px-3 py-2 text-right">
                  {workOrder.link_role === "additional" &&
                  workOrder.external_labour_charges_editable &&
                  canWrite ? (
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={drafts[workOrder.id] ?? String(workOrder.labour_cost)}
                      onChange={(event) =>
                        setDrafts((current) => ({
                          ...current,
                          [workOrder.id]: event.target.value,
                        }))
                      }
                      className="ml-auto max-w-[9rem] font-mono text-right"
                      disabled={patchPending}
                    />
                  ) : (
                    <span className="font-mono">${formatCurrency(workOrder.labour_cost)}</span>
                  )}
                  {workOrder.external_labour_locked ? (
                    <div className="text-[10px] text-slate-400">Locked after receipt</div>
                  ) : workOrder.link_role === "primary" ? (
                    <div className="text-[10px] text-slate-400">Auto-syncs from PO line</div>
                  ) : null}
                </td>
                <td className="px-3 py-2 text-right">
                  {workOrder.link_role === "additional" &&
                  workOrder.external_labour_charges_editable &&
                  canWrite ? (
                    <HubSaveButton
                      dirty={dirty}
                      dirtyHint="Change the additional labour dollars, then save."
                      onClick={() =>
                        onPatchLabour({
                          buildOrderId: workOrder.public_build_order_id,
                          chargeId: workOrder.id,
                          labourCost: draft,
                        })
                      }
                      isLoading={patchPending}
                      disabled={!dirty || patchPending}
                    >
                      Save
                    </HubSaveButton>
                  ) : (
                    <span className="text-xs text-slate-400">—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {patchError ? <p className="px-3 py-2 text-xs text-red-600">{patchError.message}</p> : null}
    </div>
  );
}

function CmPoLinkExistingWorkOrderPanel({
  cmPoId,
  items,
  canWrite,
  onAttach,
  attachPending,
  attachError,
}: {
  cmPoId: number;
  items: CmPoItemRecord[];
  canWrite: boolean;
  onAttach: (payload: {
    buildOrderId: number;
    purchaseOrderItemId: number | null;
    labourCost?: number;
  }) => void;
  attachPending: boolean;
  attachError: ApiError | null;
}) {
  const [buildOrderId, setBuildOrderId] = useState("");
  const [purchaseOrderItemId, setPurchaseOrderItemId] = useState("");
  const [labourCost, setLabourCost] = useState("");

  if (!canWrite) {
    return null;
  }

  const parsedBuildOrderId = Number(buildOrderId);
  const parsedLabour = labourCost.trim() === "" ? undefined : Number(labourCost);
  const parsedItemId =
    purchaseOrderItemId.trim() === "" ? null : Number(purchaseOrderItemId);

  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5 space-y-3">
      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-slate-600">
          Link existing work order
        </p>
        <p className="mt-1 text-xs text-slate-500">
          Attach a hub work order to this CM PO. The first PO on a work order is the primary link (external labour
          auto-syncs from the PO line until the first posted receipt). Linking this PO to a work order that already
          has a primary CM PO creates an additional labour charge (for example rework).
        </p>
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        <div>
          <Label htmlFor={`cm-po-link-wo-id-${cmPoId}`}>Hub work order id</Label>
          <Input
            id={`cm-po-link-wo-id-${cmPoId}`}
            type="number"
            min="1"
            step="1"
            value={buildOrderId}
            onChange={(event) => setBuildOrderId(event.target.value)}
            className="mt-1 font-mono"
            placeholder="8218"
          />
        </div>
        <div>
          <Label htmlFor={`cm-po-link-po-line-${cmPoId}`}>PO line (optional)</Label>
          <select
            id={`cm-po-link-po-line-${cmPoId}`}
            value={purchaseOrderItemId}
            onChange={(event) => setPurchaseOrderItemId(event.target.value)}
            className="mt-1 w-full rounded-md border border-slate-300 bg-white px-2 py-2 text-sm"
          >
            <option value="">Auto / none</option>
            {items.map((item) => (
              <option key={item.id} value={String(item.id)}>
                {item.sku} ({Number(item.quantity).toLocaleString()} @ ${formatCurrency(item.price)})
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label htmlFor={`cm-po-link-labour-${cmPoId}`}>Labour dollars (optional)</Label>
          <Input
            id={`cm-po-link-labour-${cmPoId}`}
            type="number"
            min="0"
            step="0.01"
            value={labourCost}
            onChange={(event) => setLabourCost(event.target.value)}
            className="mt-1 font-mono"
            placeholder="Defaults from PO line"
          />
        </div>
      </div>
      <div className="flex justify-end">
        <Button
          type="button"
          size="sm"
          disabled={
            attachPending ||
            !Number.isFinite(parsedBuildOrderId) ||
            parsedBuildOrderId <= 0 ||
            (parsedItemId != null && (!Number.isFinite(parsedItemId) || parsedItemId <= 0)) ||
            (parsedLabour != null && (!Number.isFinite(parsedLabour) || parsedLabour < 0))
          }
          isLoading={attachPending}
          onClick={() => {
            onAttach({
              buildOrderId: parsedBuildOrderId,
              purchaseOrderItemId: parsedItemId,
              labourCost: parsedLabour,
            });
            setBuildOrderId("");
            setPurchaseOrderItemId("");
            setLabourCost("");
          }}
        >
          Link work order
        </Button>
      </div>
      {attachError ? <p className="text-xs text-red-600">{attachError.message}</p> : null}
    </div>
  );
}

function DetailModal({ record: listRecord, onClose }: { record: CmPoRecord; onClose: () => void }) {
  const queryClient = useQueryClient();
  const canWrite = useManufacturingScopeHubWrite();

  const detailQuery = useQuery({
    queryKey: ["manufacturing", "cm-purchase-orders", listRecord.id],
    queryFn: () =>
      apiFetch<{ row: CmPoRecord }>(`/api/manufacturing/cm-purchase-orders/${listRecord.id}`).then(
        (r) => r.row,
      ),
    staleTime: 30_000,
  });

  const [draggedBuildOrderId, setDraggedBuildOrderId] = useState<number | null>(null);
  const [dropTargetId, setDropTargetId] = useState<number | "pool" | null>(null);

  const bindMutation = useMutation({
    mutationFn: ({
      buildOrderId,
      purchaseOrderItemId,
    }: {
      buildOrderId: number;
      purchaseOrderItemId: number | null;
    }) =>
      apiFetch<{ ok: boolean }>(
        `/api/manufacturing/cm-purchase-orders/${listRecord.id}/bind-work-order`,
        { method: "PATCH", body: { build_order_id: buildOrderId, purchase_order_item_id: purchaseOrderItemId } },
      ),
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: ["manufacturing", "cm-purchase-orders", listRecord.id],
      });
    },
  });

  const attachMutation = useMutation({
    mutationFn: ({
      buildOrderId,
      purchaseOrderItemId,
      labourCost,
    }: {
      buildOrderId: number;
      purchaseOrderItemId: number | null;
      labourCost?: number;
    }) =>
      apiFetch<{ ok: boolean }>(
        `/api/manufacturing/cm-purchase-orders/${listRecord.id}/attach-work-order`,
        {
          method: "POST",
          body: {
            build_order_id: buildOrderId,
            purchase_order_item_id: purchaseOrderItemId,
            ...(labourCost != null ? { labour_cost: labourCost } : {}),
          },
        },
      ),
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: ["manufacturing", "cm-purchase-orders", listRecord.id],
      });
      void queryClient.invalidateQueries({ queryKey: ["manufacturing", "cm-purchase-orders"] });
    },
  });

  const patchExternalLabourMutation = useMutation({
    mutationFn: ({
      buildOrderId,
      chargeId,
      labourCost,
    }: {
      buildOrderId: number;
      chargeId: number;
      labourCost: number;
    }) =>
      apiFetch(`/api/manufacturing/work-orders/${buildOrderId}/external-labour-cost`, {
        method: "PATCH",
        body: { labour_cost: labourCost, charge_id: chargeId },
      }),
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: ["manufacturing", "cm-purchase-orders", listRecord.id],
      });
    },
  });

  function handleDrop(buildOrderId: number, purchaseOrderItemId: number | null) {
    bindMutation.mutate({ buildOrderId, purchaseOrderItemId });
    setDraggedBuildOrderId(null);
    setDropTargetId(null);
  }

  function guessPurchaseOrderItemIdForSku(sku: string): number | null {
    const needle = sku.trim().toLowerCase();
    if (!needle) return null;
    const hit = (detailQuery.data?.items ?? listRecord.items ?? []).find((item) =>
      (item.description ?? "").toLowerCase().includes(needle),
    );
    return hit?.id ?? null;
  }

  function dropProps(targetId: number | "pool", purchaseOrderItemId: number | null) {
    return {
      onDragOver: (e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        setDropTargetId(targetId);
      },
      onDragLeave: (e: React.DragEvent) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
          setDropTargetId(null);
        }
      },
      onDrop: (e: React.DragEvent) => {
        e.preventDefault();
        const buildOrderId = Number.parseInt(e.dataTransfer.getData("text/plain"), 10);
        if (Number.isFinite(buildOrderId) && buildOrderId > 0) {
          handleDrop(buildOrderId, purchaseOrderItemId);
        }
        setDropTargetId(null);
      },
    };
  }

  const record = detailQuery.data ?? listRecord;
  const items = record.items ?? [];
  const workOrders = record.work_orders;
  const isTerminalPo = isInactivePurchaseOrderStatus(record.mv_status);

  const eligibilityQuery = useQuery({
    queryKey: ["manufacturing", "cm-purchase-orders", listRecord.id, "cancel-eligibility"],
    queryFn: () =>
      apiFetch<{ eligibility: CmPurchaseOrderCancelEligibility }>(
        `/api/manufacturing/cm-purchase-orders/${listRecord.id}/cancel-eligibility`,
      ).then((response) => response.eligibility),
    enabled: canWrite && !isTerminalPo && !detailQuery.isPending && !detailQuery.isError,
    staleTime: 15_000,
  });

  const cancelMutation = useMutation({
    mutationFn: () =>
      apiFetch<{ row: CmPoRecord }>(`/api/manufacturing/cm-purchase-orders/${listRecord.id}/cancel`, {
        method: "POST",
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["manufacturing", "cm-purchase-orders"] });
      onClose();
    },
  });

  const wosByItemId = new Map<number, CmWorkOrderRecord[]>();
  const orphanWos: CmWorkOrderRecord[] = [];
  for (const wo of workOrders) {
    if (wo.purchase_order_item_id != null) {
      const list = wosByItemId.get(wo.purchase_order_item_id) ?? [];
      list.push(wo);
      wosByItemId.set(wo.purchase_order_item_id, list);
    } else {
      orphanWos.push(wo);
    }
  }

  const totalLabour = workOrders.reduce((sum, wo) => sum + wo.labour_cost, 0);
  const isDraggingActive = draggedBuildOrderId != null;
  const locationSubtitle = formatCmPoLocationSubtitle(record.location_name, record.work_orders);

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex min-h-0 items-start justify-center overflow-y-auto bg-slate-950/70 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cm-po-detail-title"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <Card className="relative z-[101] my-4 w-full max-w-5xl shadow-xl">
        <CardHeader>
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="flex items-center gap-2">
                <h3 id="cm-po-detail-title" className="text-base font-semibold text-slate-900">
                  {record.mv_purchase_order_number ?? `PO #${record.mv_purchase_order_id}`}
                </h3>
                <a
                  href={mvPurchaseOrderUrl(record.mv_purchase_order_id, record.mv_purchase_order_number) ?? undefined}
                  target="_blank"
                  rel="noreferrer"
                  className={record.mv_purchase_order_id != null ? "text-slate-400 hover:text-brand-600" : "hidden"}
                  aria-label="Open in Megaventory"
                >
                  <ExternalLink className="h-4 w-4" />
                </a>
                <CmPoPdfLink
                  cmPoId={record.id}
                  publicPurchaseOrderId={record.public_purchase_order_id}
                  purchaseOrderNumber={record.mv_purchase_order_number}
                  iconClassName="h-4 w-4"
                />
              </div>
              <p className="text-sm text-slate-500">
                {record.supplier_name ?? "—"}
                {locationSubtitle ? ` · ${locationSubtitle}` : ""}
                {" · "}
                {formatDate(record.created_at)}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {record.mv_status ? (
                <Badge tone={statusTone(record.mv_status)}>{formatStatus(record.mv_status)}</Badge>
              ) : null}
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-slate-200 bg-white p-1 text-slate-500 hover:text-slate-900"
                aria-label="Close"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            </div>
          </div>
        </CardHeader>
        <CardBody className="space-y-3">
          {detailQuery.isPending ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-10 animate-pulse rounded-md bg-slate-100" />
              ))}
            </div>
          ) : detailQuery.isError ? (
            <p className="text-sm text-red-600">Failed to load PO details. Refresh to try again.</p>
          ) : (
            <>
              {(record.unlinked_candidate_work_orders?.length ?? 0) > 0 ? (
                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5">
                  <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-amber-800">
                    Unlinked work orders tagged to this PO
                  </p>
                  <p className="mb-2 text-xs text-amber-900/80">
                    These work orders reference this purchase order but are not on the CM PO link table. Attach them so receiving and labour stay aligned.
                  </p>
                  <ul className="space-y-1.5">
                    {(record.unlinked_candidate_work_orders ?? []).map((candidate) => (
                      <li
                        key={candidate.public_build_order_id}
                        className="flex flex-wrap items-center justify-between gap-2 rounded border border-amber-200 bg-white px-2.5 py-1.5 text-sm"
                      >
                        <div className="min-w-0">
                          <span className="font-mono text-xs font-medium text-slate-900">
                            {candidate.number}
                          </span>
                          <span className="mx-1.5 text-slate-300">·</span>
                          <span className="font-mono text-xs text-slate-700">{candidate.sku}</span>
                          <span className="mx-1.5 text-slate-300">·</span>
                          <span className="text-xs text-slate-600">
                            qty {Number(candidate.quantity).toLocaleString()}
                          </span>
                          {candidate.location_name ? (
                            <>
                              <span className="mx-1.5 text-slate-300">·</span>
                              <span className="text-xs text-slate-500">{candidate.location_name}</span>
                            </>
                          ) : null}
                        </div>
                        {canWrite ? (
                          <Button
                            type="button"
                            size="sm"
                            disabled={attachMutation.isPending}
                            isLoading={attachMutation.isPending}
                            onClick={() =>
                              attachMutation.mutate({
                                buildOrderId: candidate.public_build_order_id,
                                purchaseOrderItemId: guessPurchaseOrderItemIdForSku(candidate.sku),
                              })
                            }
                          >
                            Attach
                          </Button>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                  {attachMutation.isError ? (
                    <p className="mt-2 text-xs text-red-600">
                      Attach failed:{" "}
                      {attachMutation.error instanceof Error
                        ? attachMutation.error.message
                        : "Unknown error"}
                    </p>
                  ) : null}
                </div>
              ) : null}

              {canWrite && !isTerminalPo ? (
                <CmPoLinkExistingWorkOrderPanel
                  cmPoId={record.id}
                  items={items}
                  canWrite={canWrite}
                  attachPending={attachMutation.isPending}
                  attachError={attachMutation.error instanceof ApiError ? attachMutation.error : null}
                  onAttach={(payload) => attachMutation.mutate(payload)}
                />
              ) : null}

              {workOrders.length > 0 ? (
                <div className="space-y-2">
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    External labour links on this PO
                  </p>
                  <CmPoExternalLabourLinksPanel
                    workOrders={workOrders}
                    canWrite={canWrite}
                    patchPending={patchExternalLabourMutation.isPending}
                    patchError={
                      patchExternalLabourMutation.error instanceof ApiError
                        ? patchExternalLabourMutation.error
                        : null
                    }
                    onPatchLabour={(payload) => patchExternalLabourMutation.mutate(payload)}
                  />
                </div>
              ) : null}

              {/* Unassigned work order pool */}
              {(orphanWos.length > 0 || (isDraggingActive && canWrite)) ? (
                <div>
                  <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-slate-500">
                    Unassigned work orders
                    {canWrite ? <span className="ml-1 font-normal normal-case tracking-normal text-slate-400">— drag onto a PO line to assign</span> : null}
                  </p>
                  <div
                    {...(canWrite ? dropProps("pool", null) : {})}
                    className={[
                      "flex min-h-[36px] flex-wrap gap-1.5 rounded-md border-2 border-dashed px-2.5 py-2 transition-colors",
                      dropTargetId === "pool"
                        ? "border-amber-400 bg-amber-50"
                        : isDraggingActive && canWrite
                        ? "border-slate-300 bg-slate-50"
                        : "border-slate-200 bg-transparent",
                    ].join(" ")}
                  >
                    {orphanWos.length === 0 && isDraggingActive ? (
                      <span className="text-xs text-slate-400 self-center">Drop here to unassign</span>
                    ) : null}
                    {orphanWos.map((wo) => (
                      <WoPill
                        key={wo.id}
                        wo={wo}
                        canDrag={canWrite}
                        isDragging={draggedBuildOrderId === wo.public_build_order_id}
                        onDragStart={setDraggedBuildOrderId}
                        onDragEnd={() => { setDraggedBuildOrderId(null); setDropTargetId(null); }}
                      />
                    ))}
                  </div>
                </div>
              ) : null}

              {/* PO lines table */}
              <div className="overflow-hidden rounded-md border border-slate-200">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">SKU / description</th>
                      <th className="px-3 py-2 text-right font-medium w-20">Qty</th>
                      <th className="px-3 py-2 text-right font-medium w-24">Unit price</th>
                      <th className="px-3 py-2 text-right font-medium w-20">Rcvd</th>
                      <th className="px-3 py-2 text-left font-medium">Work orders</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {items.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-3 py-4 text-center text-slate-400">
                          No line items found.
                        </td>
                      </tr>
                    ) : null}
                    {items.map((item) => {
                      const linkedWos = wosByItemId.get(item.id) ?? [];
                      const isDropTarget = dropTargetId === item.id;
                      return (
                        <tr
                          key={item.id}
                          className={[
                            "align-top transition-colors",
                            isDropTarget ? "bg-brand-50" : "",
                          ].join(" ")}
                        >
                          <td className="px-3 py-2.5">
                            <span className="block font-mono text-xs font-medium text-slate-900">
                              {item.sku}
                            </span>
                            {item.description ? (
                              <span className="block text-xs text-slate-500 mt-0.5 leading-snug">
                                {item.description}
                              </span>
                            ) : null}
                          </td>
                          <td className="px-3 py-2.5 text-right text-slate-700">
                            {Number(item.quantity).toLocaleString()}
                          </td>
                          <td className="px-3 py-2.5 text-right font-mono text-slate-700">
                            {item.price > 0 ? `$${formatCurrency(item.price)}` : "—"}
                          </td>
                          <td className="px-3 py-2.5 text-right text-slate-600">
                            {Number(cmPoItemReceivedQuantity(item, record.system_of_record)).toLocaleString()}
                            {record.system_of_record === "hub" &&
                            item.hub_quantity_received != null &&
                            item.hub_quantity_received !== item.mv_quantity_received ? (
                              <div className="text-[10px] text-slate-400">
                                MV {Number(item.mv_quantity_received).toLocaleString()}
                              </div>
                            ) : null}
                          </td>
                          <td
                            {...(canWrite ? dropProps(item.id, item.id) : {})}
                            className={[
                              "px-3 py-2.5 transition-colors rounded-sm",
                              isDropTarget ? "ring-1 ring-inset ring-brand-400" : "",
                            ].join(" ")}
                          >
                            <div className="flex flex-wrap gap-1">
                              {linkedWos.map((wo) => (
                                <WoPill
                                  key={wo.id}
                                  wo={wo}
                                  canDrag={canWrite}
                                  isDragging={draggedBuildOrderId === wo.public_build_order_id}
                                  onDragStart={setDraggedBuildOrderId}
                                  onDragEnd={() => { setDraggedBuildOrderId(null); setDropTargetId(null); }}
                                />
                              ))}
                              {isDraggingActive && canWrite && (
                                <span className={[
                                  "inline-flex items-center rounded-full border-2 border-dashed px-2.5 py-0.5 text-xs transition-colors",
                                  isDropTarget
                                    ? "border-brand-400 bg-brand-50 text-brand-600"
                                    : "border-slate-300 text-slate-400",
                                ].join(" ")}>
                                  drop here
                                </span>
                              )}
                              {!isDraggingActive && linkedWos.length === 0 ? (
                                <span className="text-slate-400 text-xs">—</span>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  {totalLabour > 0 ? (
                    <tfoot className="border-t border-slate-200 bg-slate-50">
                      <tr>
                        <td colSpan={4} className="px-3 py-2 text-right text-xs font-medium uppercase tracking-wide text-slate-500">
                          Total external labour on this PO
                        </td>
                        <td className="px-3 py-2 font-mono text-sm font-semibold text-slate-900">
                          ${formatCurrency(totalLabour)}
                        </td>
                      </tr>
                    </tfoot>
                  ) : null}
                </table>
              </div>
              {bindMutation.isError ? (
                <p className="text-xs text-red-600">
                  Failed to save assignment: {bindMutation.error instanceof Error ? bindMutation.error.message : "Unknown error"}
                </p>
              ) : null}
              {canWrite && !isTerminalPo ? (
                <div className="flex flex-col gap-2 border-t border-slate-200 pt-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0 text-xs text-slate-500">
                    {eligibilityQuery.isPending ? "Checking whether this purchase order can be cancelled…" : null}
                    {eligibilityQuery.isError ? "Could not check cancel eligibility." : null}
                    {eligibilityQuery.data && !eligibilityQuery.data.can_cancel ? (
                      <span>{eligibilityQuery.data.reasons.join(" ")}</span>
                    ) : null}
                    {eligibilityQuery.data?.can_cancel ? (
                      <span>
                        Cancelling deletes all related work orders from Megaventory and marks them cancelled in the hub.
                      </span>
                    ) : null}
                  </div>
                  <Button
                    type="button"
                    variant="danger"
                    className="shrink-0"
                    disabled={!eligibilityQuery.data?.can_cancel || cancelMutation.isPending}
                    onClick={() => {
                      const label = record.mv_purchase_order_number ?? `PO #${record.mv_purchase_order_id}`;
                      const workOrderCount = workOrders.length;
                      if (
                        !window.confirm(
                          `Cancel ${label}? This deletes ${String(workOrderCount)} work order${workOrderCount === 1 ? "" : "s"} from Megaventory and marks them cancelled in the hub. This cannot be undone.`,
                        )
                      ) {
                        return;
                      }
                      cancelMutation.mutate();
                    }}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden />
                    Cancel purchase order
                  </Button>
                </div>
              ) : null}
              {cancelMutation.isError ? (
                <p className="text-xs text-red-600">
                  Failed to cancel: {cancelMutation.error instanceof Error ? cancelMutation.error.message : "Unknown error"}
                </p>
              ) : null}
            </>
          )}
        </CardBody>
      </Card>
    </div>,
    document.body,
  );
}

// ─── Create Modal ─────────────────────────────────────────────────────────────

function CreateModal({
  onClose,
  onSuccess,
}: {
  onClose: () => void;
  onSuccess: (record: CmPoRecord) => void;
}) {
  const hubWritesAllowed = useManufacturingScopeHubWrite();
  const operationalLocations = useOperationalLocations();
  const supplierFieldId = useId();
  const locationFieldId = useId();
  const currencyFieldId = useId();
  const commentsFieldId = useId();

  const bootstrapInitRef = useRef<CmPoCreateModalBootstrap | null>(null);
  if (bootstrapInitRef.current === null) {
    bootstrapInitRef.current = loadCmPoCreateModalBootstrap();
  }
  const init = bootstrapInitRef.current;

  const [supplierSearch, setSupplierSearch] = useState(init.supplierSearch);
  const [supplierId, setSupplierId] = useState<string>(init.supplierId);
  const [supplierLabel, setSupplierLabel] = useState<string | null>(init.supplierLabel);
  const [locationId, setLocationId] = useState<number>(init.locationId);
  const [currency, setCurrency] = useState<Currency>(init.currency);
  const [comments, setComments] = useState(init.comments);
  const [parentCmPurchaseOrderId, setParentCmPurchaseOrderId] = useState("");
  const parentFieldId = useId();
  const parentOptionsQuery = useQuery({
    queryKey: ["manufacturing", "cm-purchase-orders"],
    queryFn: () => apiFetch<{ rows: CmPoRecord[] }>("/api/manufacturing/cm-purchase-orders"),
  });
  const parentOptions = (parentOptionsQuery.data?.rows ?? []).filter(
    (row) => row.parent_cm_purchase_order_id == null,
  );
  const [lines, setLines] = useState<CmPoLineDraft[]>(init.lines);
  const [draftRestoredBanner, setDraftRestoredBanner] = useState(init.restoredMeaningfulDraft);
  const [errorBanner, setErrorBanner] = useState<string | null>(null);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      persistCmPoCreateDraft({
        v: 1,
        savedAtIso: new Date().toISOString(),
        supplierSearch,
        supplierId,
        supplierLabel,
        locationId,
        currency,
        comments,
        lines,
      });
    }, 500);
    return () => window.clearTimeout(timeoutId);
  }, [supplierSearch, supplierId, supplierLabel, locationId, currency, comments, lines]);

  const discardDraftAndReset = useCallback(() => {
    clearCmPoCreateDraftStorage();
    setSupplierSearch("");
    setSupplierId("");
    setSupplierLabel(null);
    setLocationId(DEFAULT_CM_PO_SHIP_TO_LOCATION_ID);
    setCurrency("USD");
    setComments("");
    setLines([emptyLine()]);
    setDraftRestoredBanner(false);
    setErrorBanner(null);
  }, []);

  const skuDefaultsQuery = useQuery({
    queryKey: ["manufacturing", "cm-po", "work-order-sku-defaults"],
    queryFn: () =>
      apiFetch<{ rows: { purchase_order_sku: string; work_order_skus: string[] }[] }>(
        "/api/manufacturing/cm-po/work-order-sku-defaults",
      ),
    staleTime: 120_000,
  });

  useEffect(() => {
    if (!skuDefaultsQuery.isSuccess || !skuDefaultsQuery.data.rows.length) {
      return;
    }
    const rows = skuDefaultsQuery.data.rows;
    setLines((prev) => {
      let changed = false;
      const copy = prev.map((line) => {
        const trim = line.po_sku.trim();
        if (!trim.length) {
          return line;
        }
        const presets = presetWorkOrdersForComposeLine(rows, trim);
        if (!presets || !woDraftFinishedGoodSkusAllBlank(line.work_orders)) {
          return line;
        }
        changed = true;
        return { ...line, work_orders: presets };
      });
      return changed ? copy : prev;
    });
  }, [skuDefaultsQuery.isSuccess, skuDefaultsQuery.dataUpdatedAt]);

  const supplierOmitsWorkOrderPoLabour = useMemo(() => {
    if (!supplierId) return false;
    const id = Number.parseInt(supplierId, 10);
    if (!Number.isFinite(id)) return false;
    return supplierUsesBomLabourNotPoMigrate(id, supplierLabel);
  }, [supplierId, supplierLabel]);

  const suntronicSpecialInstructions = useMemo(
    () => buildSuntronicSpecialInstructions(lines),
    [lines],
  );

  useEffect(() => {
    if (!supplierOmitsWorkOrderPoLabour) {
      return;
    }
    setComments(suntronicSpecialInstructions);
  }, [supplierOmitsWorkOrderPoLabour, suntronicSpecialInstructions]);

  const createMutation = useMutation({
    mutationFn: (body: {
      supplier_id: number;
      location_id: number;
      lines: {
        sku: string;
        quantity: number;
        unit_price: number;
        line_description?: string;
        work_orders?: { sku: string; labour_cost?: number }[];
      }[];
      currency: string;
      comments: string | null;
      parent_cm_purchase_order_id?: number | null;
    }) => apiFetch<CmPoRecord>("/api/manufacturing/cm-purchase-orders", { method: "POST", body }),
    onSuccess: (data) => {
      setErrorBanner(null);
      clearCmPoCreateDraftStorage();
      onSuccess(data);
    },
    onError: (err: Error) => {
      setErrorBanner(err.message);
    },
  });

  const formLocked = !supplierId || !hubWritesAllowed || createMutation.isPending;

  const grandTotal = lines.reduce<number>((sum, line) => {
    const total = lineTotal(line);
    return total != null ? sum + total : sum;
  }, 0);

  const hydratePurchaseOrderLineSku = useCallback(
    async (lineIndex: number, skuTrimmed: string, listRow: MrpSkuMasterRow | null) => {
      const trim = skuTrimmed.trim();
      const listHint = listRow != null ? mrpSkuMasterRowDescriptionLine(listRow) : "";
      const savedRows = skuDefaultsQuery.data?.rows;

      if (!trim.length) {
        setLines((prev) => {
          const copy = [...prev];
          const row = copy[lineIndex];
          if (!row) return prev;
          copy[lineIndex] = { ...row, po_sku: "", line_description: "" };
          return copy;
        });
        return;
      }

      if (trim.length < 2) {
        setLines((prev) => {
          const copy = [...prev];
          const row = copy[lineIndex];
          if (!row || row.po_sku.trim() !== trim) return prev;
          const presetMerged = presetWorkOrdersForComposeLine(savedRows, trim);
          copy[lineIndex] = {
            ...row,
            line_description: listHint.trim(),
            ...(presetMerged != null && woDraftFinishedGoodSkusAllBlank(row.work_orders)
              ? { work_orders: presetMerged }
              : {}),
          };
          return copy;
        });
        return;
      }

      try {
        const detail = await apiFetch<MrpSkuDetail>("/api/mrp/skus", {
          query: { sku: trim },
        });
        const segments = [
          detail.mv_description?.trim(),
          detail.generated_product_name?.trim(),
          detail.mv_variant?.trim(),
        ].filter(Boolean) as string[];
        const autoMvSuffix = segments.length > 0 ? segments.join(" · ") : listHint.trim();
        const serviceUnitPrice = formatServiceSkuUnitPriceAutofill(detail);

        setLines((prev) => {
          const copy = [...prev];
          const row = copy[lineIndex];
          if (!row || row.po_sku.trim() !== trim) return prev;
          const presetMerged = presetWorkOrdersForComposeLine(savedRows, trim);
          const shouldAutofillPrice =
            serviceUnitPrice != null && row.unit_price.trim() === "";
          copy[lineIndex] = {
            ...row,
            ...(shouldAutofillPrice ? { unit_price: serviceUnitPrice } : {}),
            line_description: autoMvSuffix,
            ...(presetMerged != null && woDraftFinishedGoodSkusAllBlank(row.work_orders)
              ? { work_orders: presetMerged }
              : {}),
          };
          return copy;
        });
      } catch {
        setLines((prev) => {
          const copy = [...prev];
          const row = copy[lineIndex];
          if (!row || row.po_sku.trim() !== trim) return prev;
          const presetMerged = presetWorkOrdersForComposeLine(savedRows, trim);
          copy[lineIndex] = {
            ...row,
            line_description: listHint.trim(),
            ...(presetMerged != null && woDraftFinishedGoodSkusAllBlank(row.work_orders)
              ? { work_orders: presetMerged }
              : {}),
          };
          return copy;
        });
      }
    },
    [skuDefaultsQuery.data?.rows],
  );

  function handleSubmit() {
    if (!supplierId) return;

    type WoPayload = { sku: string; labour_cost?: number; finished_goods_disposition: "stay" | "transfer" };
    type LinePayload = {
      sku: string;
      quantity: number;
      unit_price: number;
      line_description?: string;
      work_orders?: WoPayload[];
    };

    const parsedLines: LinePayload[] = [];

    for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
      const line = lines[lineIndex];
      const poSku = line.po_sku.trim();
      if (poSku.length === 0) continue;

      const qty = Number.parseFloat(line.quantity || "0");
      const price = Number.parseFloat(line.unit_price || "0");

      if (!Number.isFinite(qty) || qty <= 0) {
        setErrorBanner(`Quantity for purchase order SKU ${poSku} must be a positive number.`);
        return;
      }
      if (!Number.isFinite(price) || price < 0) {
        setErrorBanner(`Unit price for purchase order SKU ${poSku} must be a non-negative number.`);
        return;
      }

      const lineAmount = Math.round(qty * price * 100) / 100;
      const woDrafts = nonemptyWorkOrders(line.work_orders);
      if (woDrafts.length === 0) {
        setErrorBanner(`Line ${lineIndex + 1} (${poSku}): add at least one work order.`);
        return;
      }

      const work_orders: WoPayload[] = [];
      let labourSpecified = 0;
      let labourBlank = 0;

      if (supplierOmitsWorkOrderPoLabour) {
        for (const w of woDrafts) {
          const woSku = w.sku.trim();
          if (woSku.length === 0) {
            setErrorBanner(
              `Line ${lineIndex + 1} (${poSku}): each work order row needs a finished-good SKU.`,
            );
            return;
          }
          work_orders.push({ sku: woSku, finished_goods_disposition: w.finished_goods_disposition });
        }
      } else {
        for (const w of woDrafts) {
          const woSku = w.sku.trim();
          if (woSku.length === 0) {
            setErrorBanner(
              `Line ${lineIndex + 1} (${poSku}): each work order row needs a finished-good SKU.`,
            );
            return;
          }

          const labourStr = w.labour.trim();
          if (labourStr.length === 0) {
            labourBlank++;
          } else {
            labourSpecified++;
            const lc = Number.parseFloat(labourStr);
            if (!Number.isFinite(lc) || lc < 0) {
              setErrorBanner(
                `Line ${lineIndex + 1} (${poSku}): labour amounts must be non-negative numbers.`,
              );
              return;
            }
            work_orders.push({
              sku: woSku,
              labour_cost: Math.round(lc * 100) / 100,
              finished_goods_disposition: w.finished_goods_disposition,
            });
          }
        }

        if (labourSpecified > 0 && labourBlank > 0) {
          setErrorBanner(
            `Line ${poSku}: leave labour blank on every work order for an automatic split, or enter labour on all of them.`,
          );
          return;
        }

        if (labourBlank === woDrafts.length) {
          for (const w of woDrafts) {
            const woSku = w.sku.trim();
            work_orders.push({ sku: woSku, finished_goods_disposition: w.finished_goods_disposition });
          }
        }

        if (labourSpecified > 0) {
          const sumLab = work_orders.reduce((sum, row) => sum + (row.labour_cost ?? 0), 0);
          if (Math.round(sumLab * 100) !== Math.round(lineAmount * 100)) {
            setErrorBanner(
              `Line ${poSku}: work order labour ($${formatCurrency(sumLab)}) must equal the PO line total ($${formatCurrency(lineAmount)}).`,
            );
            return;
          }
        }
      }

      const omitWorkOrders =
        work_orders.length === 1 &&
        work_orders[0].sku === poSku &&
        (supplierOmitsWorkOrderPoLabour || labourSpecified === 0);

      const lineDescTrimmed = line.line_description.trim();

      parsedLines.push({
        sku: poSku,
        quantity: qty,
        unit_price: price,
        ...(lineDescTrimmed.length > 0 ? { line_description: lineDescTrimmed } : {}),
        ...(omitWorkOrders ? {} : { work_orders }),
      });
    }

    if (parsedLines.length === 0) {
      setErrorBanner("Add at least one purchase order line with a SKU.");
      return;
    }

    setErrorBanner(null);
    createMutation.mutate({
      supplier_id: Number.parseInt(supplierId, 10),
      location_id: locationId,
      lines: parsedLines,
      currency,
      comments: comments.trim() || null,
      parent_cm_purchase_order_id:
        parentCmPurchaseOrderId === "" ? null : Number.parseInt(parentCmPurchaseOrderId, 10),
    });
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex min-h-0 items-start justify-center overflow-y-auto bg-slate-950/70 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cm-po-create-title"
      onClick={(event) => {
        if (event.target === event.currentTarget && !createMutation.isPending) onClose();
      }}
    >
      <Card className="relative z-[101] my-4 w-full max-w-5xl shadow-xl">
        <CardHeader>
          <div className="flex items-center justify-between gap-2">
            <h3 id="cm-po-create-title" className="text-base font-semibold text-slate-900">
              Create CM PO
            </h3>
            <button
              type="button"
              onClick={onClose}
              disabled={createMutation.isPending}
              className="rounded-md border border-slate-200 bg-white p-1 text-slate-500 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50"
              aria-label="Close"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </div>
          <p className="text-sm text-slate-500">
            Each purchase order line can spawn one or more work orders with a different finished-good
            SKU.
            {supplierOmitsWorkOrderPoLabour ? (
              <>
                {" "}
                For this supplier, external labour is not written to work orders (bill of materials and
                labour SKUs carry cost in Megaventory).
              </>
            ) : (
              <>
                {" "}
                External labour for the purchase order line budget is applied to those work orders
                (split evenly if you leave labour blank, or set amounts that sum to the line total).
                Every work order uses the same quantity as the purchase order line.
              </>
            )}
            {" "}
            Your work is saved automatically as a draft in this browser until you submit. No purchase order
            or work order is created until you click Create CM PO.
          </p>
        </CardHeader>
        <CardBody>
          {draftRestoredBanner ? (
            <div className="mb-3 flex items-start justify-between gap-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950">
              <span>
                Restored draft from this browser. Nothing is created in Megaventory or the hub until you
                submit.
              </span>
              <button
                type="button"
                onClick={() => setDraftRestoredBanner(false)}
                className="shrink-0 text-xs font-medium text-amber-900 underline decoration-amber-700 hover:text-amber-950"
              >
                Dismiss
              </button>
            </div>
          ) : null}
          {errorBanner ? (
            <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900">
              {errorBanner}
            </div>
          ) : null}

          <ReadOnlyHoverLayer
            active={!hubWritesAllowed}
            hoverHint="Your role has read-only access to the Manufacturing hub module."
            layout="block"
          >
            <form
              className="space-y-4"
              onSubmit={(event) => {
                event.preventDefault();
                handleSubmit();
              }}
            >
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label htmlFor={supplierFieldId}>Supplier</Label>
                  <SupplierSearchableSelect
                    id={supplierFieldId}
                    value={supplierId}
                    disabled={!hubWritesAllowed || createMutation.isPending}
                    onValueChange={(option) => {
                      if (!option) {
                        setSupplierId("");
                        setSupplierLabel(null);
                        setSupplierSearch("");
                        return;
                      }
                      setSupplierId(option.value);
                      setSupplierLabel(option.label);
                      setSupplierSearch("");
                    }}
                    searchValue={supplierSearch}
                    onSearchChange={setSupplierSearch}
                    placeholder="Select supplier…"
                    searchPlaceholder="Search by name…"
                    emptyMessage="No suppliers match."
                    selectedLabelOverride={supplierLabel}
                  />
                </div>

                <div>
                  <Label htmlFor={locationFieldId}>Ship-to location</Label>
                  <select
                    id={locationFieldId}
                    value={locationId}
                    disabled={!hubWritesAllowed || createMutation.isPending}
                    onChange={(event) => setLocationId(Number(event.target.value))}
                    className="h-9 w-full rounded-md border border-slate-300 bg-white px-3 text-sm disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500"
                  >
                    <OperationalLocationNativeSelectOptions locations={operationalLocations.locations} />
                  </select>
                </div>
              </div>

              <div className="grid max-w-[14rem] gap-3">
                <div>
                  <Label htmlFor={currencyFieldId}>Currency</Label>
                  <select
                    id={currencyFieldId}
                    value={currency}
                    disabled={formLocked}
                    onChange={(event) => setCurrency(event.target.value as Currency)}
                    className="h-9 w-full rounded-md border border-slate-300 bg-white px-3 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500"
                  >
                    {CURRENCY_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <Label htmlFor={parentFieldId}>Parent PCBA purchase order</Label>
                <select
                  id={parentFieldId}
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                  value={parentCmPurchaseOrderId}
                  onChange={(event) => setParentCmPurchaseOrderId(event.target.value)}
                >
                  <option value="">None. This order includes the PCBA.</option>
                  {parentOptions.map((row) => (
                    <option key={row.id} value={String(row.id)}>
                      {row.mv_purchase_order_number ?? `CM PO ${row.id}`}
                      {row.supplier_name ? ` · ${row.supplier_name}` : ""}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-slate-500">
                  Set a parent when this order is a monthly TLA slice of a quarterly PCBA purchase order.
                </p>
                <Label htmlFor={commentsFieldId}>
                  {supplierOmitsWorkOrderPoLabour ? "Comments (Megaventory special instructions)" : "Comments"}
                </Label>
                {supplierOmitsWorkOrderPoLabour ? (
                  <p className="mt-0.5 text-xs text-slate-500">
                    For Suntronic, purchase order comments are built from the purchase order line SKUs below. Lines for
                    each product code are included only when the rolled-up quantity from those SKUs is greater than
                    zero.
                  </p>
                ) : null}
                <TextArea
                  id={commentsFieldId}
                  value={comments}
                  readOnly={supplierOmitsWorkOrderPoLabour}
                  aria-readOnly={supplierOmitsWorkOrderPoLabour}
                  onChange={(event) => setComments(event.target.value)}
                  placeholder={supplierOmitsWorkOrderPoLabour ? "" : "Optional notes…"}
                  disabled={formLocked}
                  className={supplierOmitsWorkOrderPoLabour ? "bg-slate-50 text-slate-800 disabled:bg-slate-100" : ""}
                />
              </div>

              {supplierOmitsWorkOrderPoLabour && supplierId ? (
                <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950">
                  This supplier uses bill-of-materials labour costing: work orders are created without
                  external labour; the purchase order line still carries unit price and extended cost.
                </p>
              ) : null}

              {/* PO lines + nested work orders */}
              <div className="space-y-3">
                <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                  Purchase order lines and work orders
                </span>

                <div className="space-y-3">
                  {lines.map((line, lineIndex) => {
                    const total = lineTotal(line);
                    const megaventoryRowDescriptionPreview = previewMegaventoryRowDescription(line);
                    return (
                      <div
                        key={lineIndex}
                        className="rounded-md border border-slate-200 bg-white p-3 shadow-sm"
                      >
                        <div className="mb-2 flex items-start justify-between gap-2">
                          <span className="text-xs font-semibold text-slate-700">
                            Line {lineIndex + 1} — Megaventory purchase order row
                          </span>
                          {lines.length > 1 ? (
                            <button
                              type="button"
                              disabled={formLocked}
                              onClick={() => setLines((prev) => prev.filter((_, i) => i !== lineIndex))}
                              className="shrink-0 rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
                              aria-label="Remove purchase order line"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          ) : null}
                        </div>

                        <div className="grid gap-2 sm:grid-cols-[1fr_minmax(0,5.5rem)_minmax(0,6rem)_minmax(0,7rem)] sm:items-end">
                          <div>
                            <div className="mb-1 flex items-center gap-1.5">
                              <Label className="text-xs text-slate-600">Purchase order SKU</Label>
                              <HoverHintContent
                                hoverHint={
                                  megaventoryRowDescriptionPreview.length > 0
                                    ? megaventoryRowDescriptionPreview
                                    : "Megaventory purchase order row description is built from work order SKUs below in order, separated by space, vertical bar, space, then the same separator followed by trailing text from this purchase order SKU once it loads. Enter work order SKUs and hover again to see the composed line."
                                }
                                cursor="help"
                                align="center"
                              >
                                <span
                                  tabIndex={0}
                                  className={[
                                    "inline-flex rounded p-0.5 outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1",
                                    megaventoryRowDescriptionPreview.length > 0
                                      ? "text-slate-500 hover:text-slate-700"
                                      : "text-slate-300",
                                  ].join(" ")}
                                  aria-label={
                                    megaventoryRowDescriptionPreview.length > 0
                                      ? "Megaventory line description for this purchase order row"
                                      : "Megaventory line description — still composing"
                                  }
                                >
                                  <HelpCircle className="h-3.5 w-3.5" aria-hidden />
                                </span>
                              </HoverHintContent>
                            </div>
                            <AssemblySkuCombobox
                              value={line.po_sku}
                              onChange={(next) => {
                                setLines((prev) => {
                                  const copy = [...prev];
                                  const row = copy[lineIndex];
                                  if (!row) return prev;
                                  const clearHint = row.po_sku.trim() !== next.trim();
                                  copy[lineIndex] = {
                                    ...row,
                                    po_sku: next,
                                    ...(clearHint ? { line_description: "" } : {}),
                                  };
                                  return copy;
                                });
                              }}
                              onPickMasterRow={(row) =>
                                void hydratePurchaseOrderLineSku(lineIndex, row.sku, row)
                              }
                              onBlurCommittedWithSku={(trimmed) =>
                                void hydratePurchaseOrderLineSku(lineIndex, trimmed, null)
                              }
                              enforceCatalogSku
                              disabled={formLocked}
                              placeholder="PO line SKU…"
                            />
                            <PurchaseOrderServiceSkuWhen sku={line.po_sku} surface="cm" />
                          </div>
                          <div>
                            <Label className="text-xs text-slate-600">Quantity</Label>
                            <Input
                              type="number"
                              min={0}
                              step="1"
                              value={line.quantity}
                              onChange={(event) => {
                                setLines((prev) => {
                                  const copy = [...prev];
                                  copy[lineIndex] = { ...copy[lineIndex], quantity: event.target.value };
                                  return copy;
                                });
                              }}
                              className="text-right font-mono text-xs"
                              disabled={formLocked}
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-slate-600">Unit price</Label>
                            <Input
                              type="number"
                              min={0}
                              step="any"
                              value={line.unit_price}
                              onChange={(event) => {
                                setLines((prev) => {
                                  const copy = [...prev];
                                  copy[lineIndex] = {
                                    ...copy[lineIndex],
                                    unit_price: event.target.value,
                                  };
                                  return copy;
                                });
                              }}
                              className="text-right font-mono text-xs"
                              placeholder="0.00"
                              disabled={formLocked}
                            />
                          </div>
                          <div className="text-right pb-2">
                            <span className="block text-xs text-slate-500">Line total / labour budget</span>
                            <span className="font-mono text-sm font-medium text-slate-900">
                              {total != null ? `$${formatCurrency(total)}` : "—"}
                            </span>
                          </div>
                        </div>

                        <div className="mt-4 border-t border-slate-100 pt-3">
                          <div className="space-y-2">
                            {line.work_orders.map((wo, woIndex) => (
                              <div
                                key={woIndex}
                                className="grid gap-2 sm:grid-cols-[1fr_minmax(0,11rem)_minmax(0,7rem)_auto] sm:items-end"
                              >
                                <div>
                                  <Label className="text-xs text-slate-600">Work order SKU</Label>
                                  <AssemblySkuCombobox
                                    value={wo.sku}
                                    onChange={(next) => {
                                      setLines((prev) => {
                                        const copy = [...prev];
                                        const w = [...copy[lineIndex].work_orders];
                                        w[woIndex] = { ...w[woIndex], sku: next };
                                        copy[lineIndex] = { ...copy[lineIndex], work_orders: w };
                                        return copy;
                                      });
                                    }}
                                    enforceCatalogSku
                                    disabled={formLocked}
                                    placeholder="Finished good SKU…"
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs text-slate-600">After receive</Label>
                                  <select
                                    className="mt-1 w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-800"
                                    value={wo.finished_goods_disposition}
                                    disabled={formLocked}
                                    onChange={(event) => {
                                      const next = event.target.value === "stay" ? "stay" : "transfer";
                                      setLines((prev) => {
                                        const copy = [...prev];
                                        const w = [...copy[lineIndex].work_orders];
                                        w[woIndex] = { ...w[woIndex], finished_goods_disposition: next };
                                        copy[lineIndex] = { ...copy[lineIndex], work_orders: w };
                                        return copy;
                                      });
                                    }}
                                  >
                                    <option value="transfer">Transfer to SGS</option>
                                    <option value="stay">Stays at contract manufacturer</option>
                                  </select>
                                </div>
                                <div>
                                  <HoverHintContent
                                    hoverHint={
                                      supplierOmitsWorkOrderPoLabour
                                        ? "Suntronic: labour is not migrated to work orders. Leave this blank."
                                        : "Leave blank to split the line total evenly across work orders. If you enter an amount on one row, enter labour on every row; they must sum to the line total."
                                    }
                                    cursor="help"
                                  >
                                    <Label className="text-xs text-slate-600">Labour</Label>
                                  </HoverHintContent>
                                  <Input
                                    type="number"
                                    min={0}
                                    step="any"
                                    value={wo.labour}
                                    onChange={(event) => {
                                      setLines((prev) => {
                                        const copy = [...prev];
                                        const w = [...copy[lineIndex].work_orders];
                                        w[woIndex] = { ...w[woIndex], labour: event.target.value };
                                        copy[lineIndex] = { ...copy[lineIndex], work_orders: w };
                                        return copy;
                                      });
                                    }}
                                    className="text-right font-mono text-xs"
                                    placeholder={supplierOmitsWorkOrderPoLabour ? "N/A" : "Auto"}
                                    disabled={formLocked || supplierOmitsWorkOrderPoLabour}
                                  />
                                </div>
                                <div className="flex justify-end pb-1">
                                  {line.work_orders.length > 1 ? (
                                    <button
                                      type="button"
                                      disabled={formLocked}
                                      onClick={() => {
                                        setLines((prev) => {
                                          const copy = [...prev];
                                          const w = copy[lineIndex].work_orders.filter(
                                            (_, j) => j !== woIndex,
                                          );
                                          copy[lineIndex] = { ...copy[lineIndex], work_orders: w };
                                          return copy;
                                        });
                                      }}
                                      className="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
                                      aria-label="Remove work order row"
                                    >
                                      <X className="h-4 w-4" />
                                    </button>
                                  ) : (
                                    <span className="w-6" />
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            className="mt-2"
                            disabled={formLocked}
                            onClick={() => {
                              setLines((prev) => {
                                const copy = [...prev];
                                copy[lineIndex] = {
                                  ...copy[lineIndex],
                                  work_orders: [...copy[lineIndex].work_orders, emptyWo()],
                                };
                                return copy;
                              });
                            }}
                          >
                            <Plus className="mr-1 h-3.5 w-3.5" />
                            Add work order for this line
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {grandTotal > 0 ? (
                  <div className="flex justify-end border-t border-slate-200 pt-2">
                    <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                      PO lines total &nbsp;
                    </span>
                    <span className="font-mono text-sm font-semibold text-slate-900">
                      ${formatCurrency(grandTotal)}
                    </span>
                  </div>
                ) : null}

                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  disabled={formLocked}
                  onClick={() => setLines((prev) => [...prev, emptyLine()])}
                >
                  <Plus className="mr-1 h-3.5 w-3.5" />
                  Add purchase order line
                </Button>
              </div>

              <div className="flex flex-col gap-3 border-t border-slate-100 pt-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="max-w-xl text-xs text-slate-500">
                  Draft saves in this browser. No purchase order or work order exists until you click Create
                  CM PO.
                </p>
                <div className="flex flex-wrap justify-end gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={discardDraftAndReset}
                    disabled={createMutation.isPending}
                  >
                    Discard draft
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={onClose}
                    disabled={createMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    size="sm"
                    disabled={!supplierId || !hubWritesAllowed || createMutation.isPending}
                    isLoading={createMutation.isPending}
                  >
                    Create CM PO
                  </Button>
                </div>
              </div>
            </form>
          </ReadOnlyHoverLayer>
        </CardBody>
      </Card>
    </div>,
    document.body,
  );
}

// ─── Success Modal ────────────────────────────────────────────────────────────

function SuccessModal({ record, onClose }: { record: CmPoRecord; onClose: () => void }) {
  const locationSubtitle = formatCmPoLocationSubtitle(record.location_name, record.work_orders);
  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex min-h-0 items-start justify-center overflow-y-auto bg-slate-950/70 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <Card className="relative z-[101] my-4 w-full max-w-lg shadow-xl">
        <CardHeader>
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              <h3 className="text-base font-semibold text-slate-900">CM PO created</h3>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-slate-200 bg-white p-1 text-slate-500 hover:text-slate-900"
              aria-label="Close"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </div>
        </CardHeader>
        <CardBody className="space-y-3">
          <p className="text-sm text-slate-700">
            Purchase order{" "}
            <span className="inline-flex items-center gap-1 align-middle">
              <span className="font-mono font-medium">
                {record.mv_purchase_order_number ?? `MV #${record.mv_purchase_order_id}`}
              </span>
              <a
                href={mvPurchaseOrderUrl(record.mv_purchase_order_id, record.mv_purchase_order_number) ?? undefined}
                target="_blank"
                rel="noreferrer"
                className={record.mv_purchase_order_id != null ? "text-slate-400 hover:text-brand-600" : "hidden"}
                aria-label="Open purchase order in Megaventory"
              >
                <ExternalLink className="h-4 w-4" />
              </a>
              <CmPoPdfLink
                cmPoId={record.id}
                publicPurchaseOrderId={record.public_purchase_order_id}
                purchaseOrderNumber={record.mv_purchase_order_number}
                iconClassName="h-4 w-4"
              />
            </span>{" "}
            and {record.work_orders.length} work order
            {record.work_orders.length !== 1 ? "s" : ""} created
            {locationSubtitle ? ` (${locationSubtitle})` : ""}
            .
          </p>
          {record.parent_mv_purchase_order_number ? (
            <p className="text-sm text-slate-700">
              TLA child of PCBA purchase order{" "}
              <span className="font-mono font-medium">{record.parent_mv_purchase_order_number}</span>.
            </p>
          ) : null}
          <div className="divide-y divide-slate-100 rounded-md border border-slate-200">
            {record.work_orders.map((wo) => (
              <div
                key={wo.id}
                className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-sm sm:flex-nowrap sm:justify-between"
              >
                <div className="flex min-w-0 flex-1 items-baseline gap-2">
                  <span className="font-mono text-slate-900">{wo.sku}</span>
                  <span className="whitespace-nowrap text-slate-500">Quantity {wo.quantity}</span>
                </div>
                <div className="flex flex-shrink-0 items-center gap-2 text-slate-600 sm:gap-3">
                  <span>
                    Labour{" "}
                    <span className="font-medium text-slate-900">
                      ${formatCurrency(wo.labour_cost)}
                    </span>
                  </span>
                  <Badge tone="neutral">
                    <span className="font-mono">{wo.mv_work_order_number}</span>
                  </Badge>
                  <a
                    href={mvWorkOrderUrl(wo.mv_work_order_id, wo.mv_work_order_number)}
                    target="_blank"
                    rel="noreferrer"
                    className="text-slate-400 hover:text-brand-600"
                    aria-label="Open work order in Megaventory"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-end">
            <Button type="button" size="sm" onClick={onClose}>
              Done
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>,
    document.body,
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function CmPurchaseOrderListPage() {
  const hubWritesAllowed = useManufacturingScopeHubWrite();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showCreate, setShowCreate] = useState(false);
  const [successRecord, setSuccessRecord] = useState<CmPoRecord | null>(null);
  const [detailRecord, setDetailRecord] = useState<CmPoRecord | null>(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<CmPoStatusFilter>("active");

  const listQuery = useQuery({
    queryKey: ["manufacturing", "cm-purchase-orders"],
    queryFn: () => apiFetch<{ rows: CmPoRecord[] }>("/api/manufacturing/cm-purchase-orders"),
  });

  const rows = listQuery.data?.rows ?? [];
  const deepLinkedCmPoId = cmPurchaseOrderIdFromSearchParams(searchParams);

  useEffect(() => {
    if (deepLinkedCmPoId == null || detailRecord?.id === deepLinkedCmPoId) return;
    const linkedRecord = rows.find((row) => Number(row.id) === deepLinkedCmPoId);
    if (linkedRecord) setDetailRecord(linkedRecord);
  }, [deepLinkedCmPoId, detailRecord?.id, rows]);

  const openDetail = useCallback(
    (record: CmPoRecord) => {
      const next = new URLSearchParams(searchParams);
      next.set("cm_po_id", String(record.id));
      setSearchParams(next);
      setDetailRecord(record);
    },
    [searchParams, setSearchParams],
  );

  const closeDetail = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete("cm_po_id");
    setSearchParams(next, { replace: true });
    setDetailRecord(null);
  }, [searchParams, setSearchParams]);

  // Distinct statuses present in the data, in a stable display order
  const STATUS_ORDER = ["Pending", "Open", "Verified", "PartiallyReceivedAndPartiallyInvoiced", "FullyInvoiced", "Closed", "Cancelled"];
  const distinctStatuses = STATUS_ORDER.filter((s) =>
    rows.some((row) => row.mv_status === s),
  );
  // Also catch any statuses not in the order list
  const extraStatuses = [...new Set(rows.map((r) => r.mv_status).filter((s): s is string => s != null))].filter(
    (s) => !STATUS_ORDER.includes(s),
  );
  const allDistinctStatuses = [...distinctStatuses, ...extraStatuses];

  const filteredRows = rows.filter((row) => {
    if (statusFilter === "active" && isInactivePurchaseOrderStatus(row.mv_status)) return false;
    if (statusFilter !== "active" && statusFilter !== "all" && row.mv_status !== statusFilter) return false;
    const needle = search.trim().toLowerCase();
    if (!needle) return true;
    if ((row.mv_purchase_order_number ?? "").toLowerCase().includes(needle)) return true;
    if ((row.supplier_name ?? "").toLowerCase().includes(needle)) return true;
    if ((row.location_name ?? "").toLowerCase().includes(needle)) return true;
    if (
      uniqueWorkOrderLocationNames(row.work_orders).some((name) => name.toLowerCase().includes(needle))
    ) {
      return true;
    }
    if (row.work_orders.some((wo) => wo.sku.toLowerCase().includes(needle))) return true;
    return false;
  });

  return (
    <>
      <div className="space-y-4">
        <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-900">CM PO</h1>
            <p className="text-sm text-slate-500">Contract manufacturing purchase orders</p>
          </div>
          {hubWritesAllowed ? (
            <Button
              type="button"
              size="sm"
              onClick={() => setShowCreate(true)}
            >
              <Plus className="mr-1.5 h-4 w-4" />
              Create CM PO
            </Button>
          ) : null}
        </header>

        <Card>
          <CardHeader>
            <CardTitle>Filters</CardTitle>
          </CardHeader>
          <CardBody className="space-y-3">
            <div className="grid gap-3 lg:grid-cols-[minmax(16rem,24rem)_1fr]">
              <div>
                <Label htmlFor="cm-po-search">Search CM purchase orders</Label>
                <Input
                  id="cm-po-search"
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Filter by PO number, supplier, ship-to, work order location, or SKU..."
                  autoComplete="off"
                />
              </div>
              <div>
                <Label>Status</Label>
                <div className="mt-1 flex flex-wrap gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant={statusFilter === "active" ? "primary" : "secondary"}
                    onClick={() => setStatusFilter("active")}
                  >
                    Active
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={statusFilter === "all" ? "primary" : "secondary"}
                    onClick={() => setStatusFilter("all")}
                  >
                    All
                  </Button>
                  {allDistinctStatuses.map((status) => (
                    <Button
                      key={status}
                      type="button"
                      size="sm"
                      variant={statusFilter === status ? "primary" : "secondary"}
                      onClick={() => setStatusFilter(status)}
                    >
                      {formatStatus(status)}
                      <span className="text-xs opacity-70">{rows.filter((r) => r.mv_status === status).length}</span>
                    </Button>
                  ))}
                </div>
              </div>
            </div>
            <p className="text-xs text-slate-500">
              Showing {filteredRows.length} of {rows.length} CM purchase order{rows.length === 1 ? "" : "s"}.
            </p>
          </CardBody>
        </Card>

        <Card>
          <CardBody className="overflow-x-auto p-0">
            {listQuery.isError ? (
              <div className="px-4 py-6 text-center text-sm text-red-600">
                <p>Failed to load contract manufacturing purchase orders.</p>
                {listQuery.error instanceof ApiError ? (
                  <p className="mt-1 font-mono text-xs text-red-700">
                    HTTP {listQuery.error.status}: {listQuery.error.message}
                  </p>
                ) : listQuery.error instanceof Error ? (
                  <p className="mt-1 font-mono text-xs text-red-700">{listQuery.error.message}</p>
                ) : null}
                <p className="mt-2 text-xs text-slate-500">
                  Open the browser network tab for <span className="font-mono">/api/manufacturing/cm-purchase-orders</span>{" "}
                  to see the full response, or refresh after signing in again.
                </p>
              </div>
            ) : rows.length === 0 && !listQuery.isPending ? (
              <p className="px-4 py-10 text-center text-sm text-slate-500">
                No CM POs yet.{" "}
                {hubWritesAllowed ? (
                  <button
                    type="button"
                    onClick={() => setShowCreate(true)}
                    className="text-brand-600 hover:underline"
                  >
                    Create the first one.
                  </button>
                ) : null}
              </p>
            ) : (
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-3 py-2 font-medium">PO number</th>
                    <th className="px-3 py-2 font-medium">Parent</th>
                    <th className="px-3 py-2 font-medium">Supplier</th>
                    <th className="px-3 py-2 font-medium">Location</th>
                    <th className="px-3 py-2 font-medium text-right">Work orders</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                    <th className="px-3 py-2 font-medium">Created</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {listQuery.isPending
                    ? Array.from({ length: 3 }).map((_, i) => (
                        <tr key={i} className="animate-pulse">
                          {Array.from({ length: 7 }).map((__, j) => (
                            <td key={j} className="px-3 py-3">
                              <div className="h-3 rounded bg-slate-100" />
                            </td>
                          ))}
                        </tr>
                      ))
                    : filteredRows.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="px-3 py-8 text-center text-sm text-slate-500">
                            No CM purchase orders match these filters.
                          </td>
                        </tr>
                      )
                    : filteredRows.map((row) => (
                        <tr
                          key={row.id}
                          className="cursor-pointer hover:bg-slate-50 transition-colors"
                          onClick={() => openDetail(row)}
                        >
                          <td className="px-3 py-2.5">
                            <div className="flex items-center gap-1.5">
                              <span className="font-mono text-xs font-medium text-brand-700">
                                {row.mv_purchase_order_number ?? `MV #${row.mv_purchase_order_id}`}
                              </span>
                              <a
                                href={mvPurchaseOrderUrl(row.mv_purchase_order_id, row.mv_purchase_order_number) ?? undefined}
                                target="_blank"
                                rel="noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className={row.mv_purchase_order_id != null ? "text-slate-400 hover:text-brand-600" : "hidden"}
                                aria-label="Open in Megaventory"
                              >
                                <ExternalLink className="h-3 w-3" />
                              </a>
                              <CmPoPdfLink
                                cmPoId={row.id}
                                publicPurchaseOrderId={row.public_purchase_order_id}
                                purchaseOrderNumber={row.mv_purchase_order_number}
                                iconClassName="h-3 w-3"
                                stopRowClick
                              />
                            </div>
                          </td>
                          <td className="px-3 py-2.5 font-mono text-xs text-slate-600">
                            {row.parent_mv_purchase_order_number ?? "—"}
                          </td>
                          <td className="px-3 py-2.5 text-slate-700">
                            {row.supplier_name ?? "—"}
                          </td>
                          <td className="px-3 py-2.5 text-slate-600">
                            {formatCmPoLocationSubtitle(row.location_name, row.work_orders) || "—"}
                          </td>
                          <td className="px-3 py-2.5 text-right text-slate-700">
                            {row.work_orders.length}
                          </td>
                          <td className="px-3 py-2.5">
                            {row.mv_status ? (
                              <Badge tone={statusTone(row.mv_status)}>{row.mv_status}</Badge>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2.5 text-slate-500">
                            {formatDate(row.created_at)}
                          </td>
                        </tr>
                      ))}
                </tbody>
              </table>
            )}
          </CardBody>
        </Card>
      </div>

      {showCreate ? (
        <CreateModal
          onClose={() => setShowCreate(false)}
          onSuccess={(record) => {
            setShowCreate(false);
            setSuccessRecord(record);
            void queryClient.invalidateQueries({
              queryKey: ["manufacturing", "cm-purchase-orders"],
            });
          }}
        />
      ) : null}

      {successRecord ? (
        <SuccessModal record={successRecord} onClose={() => setSuccessRecord(null)} />
      ) : null}

      {detailRecord ? (
        <DetailModal record={detailRecord} onClose={closeDetail} />
      ) : null}
    </>
  );
}
